import {
  Button,
  Dialog,
  Empty,
  Flex,
  Grid,
  Input,
  Loader,
  Modal,
  Spacer,
  Spinner,
  Text,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { IDesign } from 'common/interfaces/hierarchy-entities';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SyncDesignsTreeItem } from './sync-designs-tree-item';
import { buildSyncTree, filterSyncTree } from './sync-designs-tree.utils';
import { TAncestorLevel } from './design-sync.utils';
type SyncDesignModalProps = {
  entityType: TAncestorLevel,
  onConfirmSyncStartSelect: (designids: string[]) => void,
  onClose: () => void,
  designs: IDesign[],
  isLoading: boolean,
  designSyncStatuses: { [key: string]: boolean }
};

export const SyncDesignModal = ({
  entityType,
  onClose,
  onConfirmSyncStartSelect,
  designs,
  isLoading,
  designSyncStatuses,
}: SyncDesignModalProps) => {
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const { t } = useTranslation();

  const nodes = useMemo(() => {
    return buildSyncTree(designs, entityType);
  }, [designs, entityType]);
  const visibleNodes = useMemo(() => {
    return filterSyncTree(nodes, deferredSearchTerm);
  }, [nodes, deferredSearchTerm]);

  const visibleSelectableIds = Array.from(new Set(visibleNodes.flatMap((node) => node.designIds)))
    .filter((designid) => !designSyncStatuses[designid]);
  const checkedVisibleCount = visibleSelectableIds.filter((designid) => checkedIds.has(designid)).length;
  const isAllVisibleChecked = visibleSelectableIds.length > 0 && checkedVisibleCount === visibleSelectableIds.length;

  const handleSelectionChange = useCallback((changedIds: string[], checked: boolean) => {
    setCheckedIds((previous) => {
      const next = new Set(previous);
      changedIds.forEach((designid) => {
        if (checked) {
          next.add(designid);
        } else {
          next.delete(designid);
        }
      });
      return next;
    });
  }, []);

  const handleDesignSync = () => {
    onConfirmSyncStartSelect(Array.from(checkedIds));
    onClose();
  };

  const renderContent = () => {
    if (nodes.length === 0) {
      if (isLoading) {
        return <Loader height="50px" theme="light"><Spinner dark /></Loader>;
      }
      return <Text muted>{t(translations.design_noDesignsToSync)}</Text>;
    }
    return (
      <>
          <Grid columns="1fr auto" gap>
            <Input
              icon="search"
              placeholder={t(translations.search)}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <Button
              basic
              label={`${t(isAllVisibleChecked ? translations.deselect : translations.selectAll)} (${visibleSelectableIds.length})`}
              disabled={visibleSelectableIds.length === 0}
              onClick={() => handleSelectionChange(visibleSelectableIds, !isAllVisibleChecked)}
            />
          </Grid>
          <Spacer />
        {visibleNodes.length === 0
          ? (
            <Flex justifyContent="center" alignItems="center">
              <Empty text={t(translations.design_noDesignsToSync)} />
            </Flex>
          )
          : visibleNodes.map((node) => (
            <SyncDesignsTreeItem
              key={node.id}
              node={node}
              expandAll={!!deferredSearchTerm.trim()}
              checkedIds={checkedIds}
              syncStatuses={designSyncStatuses}
              onChange={handleSelectionChange}
            />
          ))}
      </>
    );
  };

  return (
    <Modal visible onEscape={onClose}>
      <Dialog
        dialog={{
          scroll: true,
          width: 'calc(var(--size-xl) * 10)',
          heading: t(translations.designs),
          content: renderContent(),
          footer: (
            <Flex alignItems="center" width="100%">
              <Button colored label={t(translations.design_syncDesigns)} onClick={handleDesignSync} disabled={!checkedIds.size} />
              <Button label={t(translations.cancel)} onClick={onClose} />
              <Spacer flex />
              {designs.length > 0 && (
                <Flex width="calc(var(--size-xl) * 4)" justifyContent="flex-end">
                  <Text muted>{t(translations.design_selectedCount, { selected: checkedIds.size, total: designs.length })}</Text>
                </Flex>
              )}
            </Flex>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};
